import { Button, Divider, Grid, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  REMINDER_NOTES_STATUS_COLOR_MAP,
  REMINDER_TYPE_STATUS_MESSAGE_MAP,
  TOAST_SEVERITY,
} from "static/constants";

import { getDateAndTimeWithAMPM } from "utils/date";
import { showToast } from "utils/toast";

import * as patientService from "app/services/patient/patient";

import SubmitButton from "components/Button/SubmitButton";
import EmptyContainer from "components/common/empty_container/EmptyContainer";
import GridListItem from "components/common/grid/GridListItem";
import Loading from "components/common/loading/Loading";
import {
  useGetPatientNotesHistoryQuery,
  useGetPatientReminderHistoryQuery,
} from "app/store/patient/api/patientApi";
import CustomTabs from "components/common/tabs/CustomTabs";
import { mapReminderHistoryStatus } from "utils/patientReminder.util";
import {
  setUpdatedPatientNotes,
  setScrollToNotesSection,
} from "app/store/transmission/slice/transmissionSlice";
import RequestDeleteNoteModal from "./RequestDeleteNoteModal";

const PatientNotes = () => {
  const NOTE_HISTORY_TABS = [
    {
      label: "ALL",
      value: "",
    },
    {
      label: "NOTES",
      value: "notes",
    },
    {
      label: "AUTOMATED REMINDERS",
      value: "reminders",
    },
    {
      label: "LIVE CALLS",
      value: "liveCalls",
    },
    {
      label: "TASK NOTES",
      value: "taskNotes",
    },
  ];

  const dispatch = useDispatch();

  const { patientId, patientCommunicationAndNotes, scrollToNotesSection } =
    useSelector((state) => state.transmission);

  const { currentUser } = useSelector((state) => state.auth);

  const sectionRef = useRef(null);

  const [note, setNote] = useState(
    patientCommunicationAndNotes.updatedNotes || ""
  );

  const [activeTab, setActiveTab] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [consolidatedNoteHistory, setConsolidatedNoteHistory] = useState([]);
  const [activeNoteHistory, setActiveNoteHistory] = useState(
    consolidatedNoteHistory || []
  );

  const {
    data: reminderHistory,
    isLoading: isReminderHistoryLoading,
    isfetching: isReminderHistoryFetching,
    refetch: refetchReminderHistory,
    status: reminderHistoryStatus,
  } = useGetPatientReminderHistoryQuery(
    { patientId },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: notesHistory,
    isLoading: isNotesHistoryLoading,
    isFetching: isNotesHistoryFetching,
    refetch: refetchNotesHistory,
    status: notesHistoryStatus,
  } = useGetPatientNotesHistoryQuery(
    { patientId },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (scrollToNotesSection && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      if (notesHistoryStatus !== "uninitialized") refetchNotesHistory();
      if (reminderHistoryStatus !== "uninitialized") refetchReminderHistory();
      dispatch(setScrollToNotesSection(false));
    }
  }, [
    scrollToNotesSection,
    dispatch,
    notesHistoryStatus,
    reminderHistoryStatus,
  ]);

  const isRemindersAndNotesLoading =
    isNotesHistoryLoading ||
    isReminderHistoryLoading ||
    isReminderHistoryFetching ||
    isNotesHistoryFetching;

  useEffect(() => {
    const consolidatedHistory = consolidateNoteHistory(
      notesHistory?.histories?.length ? notesHistory?.histories : [],
      reminderHistory?.logs?.length ? reminderHistory?.logs : []
    );

    setConsolidatedNoteHistory(consolidatedHistory);
    handleNoteHistoryTabChange("", consolidatedHistory);
  }, [reminderHistory, notesHistory]);

  const consolidateNoteHistory = (notesHistory, reminderHistory) => {
    const consolidatedNoteHistory = [];

    notesHistory.forEach((note) => {
      const { addedAt, addedBy, note: noteContent, taskId } = note;
      const { date, time } = getDateAndTimeWithAMPM(addedAt);

      consolidatedNoteHistory.push({
        addedAt,
        date,
        time,
        addedBy,
        note: noteContent,
        type: taskId ? "Task Communication Note" : "Manual Note",
        noteId: note?._id,
        isDeleteRequest: note?.isDeleteRequest,
      });
    });

    reminderHistory.forEach((reminder) => {
      const {
        status,
        duration,
        type: reminder_type,
        created_at: addedAt,
        created_by: addedBy,
        success_message,
        error_message,
        message: messageNote,
      } = reminder;

      const { date, time } = getDateAndTimeWithAMPM(addedAt);
      const logStatus = mapReminderHistoryStatus(status);

      let message;
      if (logStatus === "Sent") {
        message = success_message || messageNote;
      } else if (logStatus === "Failed") {
        message = error_message || messageNote;
      } else if (logStatus === "Follow Up") {
        message = messageNote;
      }

      consolidatedNoteHistory.push({
        addedAt,
        date,
        time,
        addedBy,
        duration,
        status: logStatus,
        note: message || REMINDER_TYPE_STATUS_MESSAGE_MAP[logStatus],
        type: reminder_type,
      });
    });

    consolidatedNoteHistory.sort(
      (a, b) => new Date(b.addedAt) - new Date(a.addedAt)
    );

    return consolidatedNoteHistory;
  };

  const handleAddNote = async () => {
    try {
      setIsLoading(true);
      await patientService.addPatientNote({
        patientId,
        note: note.trim(),
      });
      refetchNotesHistory();
      handleNoteHistoryTabChange("notes");
      showToast({
        message: "Note saved successfully.",
        type: TOAST_SEVERITY.SUCCESS,
      });
    } catch (err) {
      showToast({ message: err.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      setIsLoading(false);
      setNote("");
      dispatch(setUpdatedPatientNotes({ note: "" }));
    }
  };

  const handleNoteHistoryTabChange = (tabName, noteHistory) => {
    let activeNoteList;
    if (!tabName) {
      activeNoteList = noteHistory || consolidatedNoteHistory;
    }

    if (tabName === "notes") {
      activeNoteList = consolidatedNoteHistory.filter(
        (note) => note.type === "Manual Note"
      );
    }

    if (tabName === "reminders") {
      activeNoteList = consolidatedNoteHistory.filter((note) =>
        [
          "First Reminder",
          "Second Reminder",
          "Third Reminder",
          "Letter",
        ].includes(note.type)
      );
    }

    if (tabName === "liveCalls") {
      activeNoteList = consolidatedNoteHistory.filter(
        (note) => note.type === "Live Call"
      );
    }

    if (tabName === "taskNotes") {
      activeNoteList = consolidatedNoteHistory.filter(
        (note) => note?.type === "Task Communication Note"
      );
    }

    setActiveTab(tabName);
    setActiveNoteHistory(activeNoteList);
  };

  const getStatusColor = (status) =>
    REMINDER_NOTES_STATUS_COLOR_MAP[status?.toLowerCase()] || "#ff0000";

  const [selectedNote, setSelectedNote] = useState(null);
  const [showRequestDeleteModal, setShowRequestDeleteModal] = useState(false);

  const handleRequestToDelete = (note) => {
    setSelectedNote(note);
    setShowRequestDeleteModal(true);
  };

  const handleCancelRequestDelete = () => {
    setSelectedNote(null);
    setShowRequestDeleteModal(false);
  };

  return (
    <Grid
      ref={sectionRef}
      container
      className="patient-modal__aside-container mb-3"
    >
      <div
        style={{
          fontWeight: 500,
          fontSize: "18px",
          textAlign: "left",
          marginBottom: "12px !important",
        }}
      >
        Communication And Notes History
      </div>

      <Grid container spacing={2}>
        <Divider className="" />
        <Grid item xs={4}>
          <TextField
            value={note}
            className="view-pdf__textarea mt-2"
            placeholder="Type here to add notes."
            id="outlined-multiline-static"
            data-text="communication-note-field"
            multiline
            rows="13"
            fullWidth
            margin="normal"
            name="notes"
            variant="outlined"
            onChange={(e) => {
              const value = e.target.value;
              setNote(value);

              if (
                !!value.trim() !== !!patientCommunicationAndNotes.updatedNotes
              ) {
                dispatch(setUpdatedPatientNotes({ note: value.trim() }));
              }
            }}
            onBlur={() => {
              return dispatch(setUpdatedPatientNotes({ note: note.trim() }));
            }}
            disabled={isLoading || isNotesHistoryLoading}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
              },
            }}
          />
          <div style={{ textAlign: "right" }}>
            <SubmitButton
              className="ml-2"
              variant="contained"
              color="secondary"
              handleClick={handleAddNote}
              isLoading={isLoading}
              disabled={note.trim() === "" || isNotesHistoryLoading}
              buttonText="Add Notes"
              data-action="communication-note-add-button"
            />
          </div>
        </Grid>

        <Grid item xs={8}>
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
            className="mt-2"
          >
            <div
              style={{
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <CustomTabs
                tabs={NOTE_HISTORY_TABS}
                selectedTabs={activeTab}
                showContent={false}
                onChange={(newTab) => handleNoteHistoryTabChange(newTab)}
              />
            </div>

            <div
              style={{
                flex: 1,
                padding: "16px",
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #E0E0E0",
                borderTop: "none",
              }}
            >
              {isRemindersAndNotesLoading && <Loading />}

              {!activeNoteHistory.length && !isRemindersAndNotesLoading && (
                <EmptyContainer
                  height="100%"
                  noDataTextStyles={{
                    textAlign: "center",
                  }}
                  noDataText={
                    <>
                      <h3 className="empty__title">
                        Communication notes not found.
                      </h3>
                    </>
                  }
                />
              )}

              {activeNoteHistory.length > 0 && !isRemindersAndNotesLoading && (
                <>
                  {activeNoteHistory.map((data, index) => {
                    const {
                      note,
                      addedBy,
                      date,
                      time,
                      type,
                      status,
                      isDeleteRequest,
                    } = data;

                    const noteTitle =
                      (addedBy ? addedBy + " • " : "") + date + " • " + time;

                    return (
                      <>
                        <GridListItem
                          key={index}
                          marginBottom="8px"
                          primaryTextColor="#949494"
                          secondaryTextColor="#444444"
                          secondaryFontWeight={400}
                          title={
                            <div className="d-flex justify-between">
                              <div>
                                {type}
                                {status && (
                                  <>
                                    {" • "}
                                    <span
                                      style={{
                                        color: getStatusColor(status),
                                      }}
                                    >
                                      {status}
                                    </span>
                                  </>
                                )}
                              </div>
                              <div>{noteTitle}</div>
                            </div>
                          }
                          subTitle={
                            <div className="d-flex justify-between align-items-center g-1">
                              <div>{note}</div>

                              {[
                                "Manual Note",
                                "Task Communication Note",
                              ].includes(type) &&
                                addedBy === currentUser?.name && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    className="d-flex justify-end align-center"
                                    disabled={isDeleteRequest}
                                    onClick={() =>
                                      handleRequestToDelete({
                                        noteId: data?.noteId,
                                        content: note,
                                        type,
                                        date,
                                        time,
                                        patientId,
                                        addedBy,
                                      })
                                    }
                                    sx={{
                                      borderRadius: "4px",
                                      padding: "4px 8px",
                                      fontFamily: "Roboto",
                                      fontWeight: 500,
                                      fontSize: "12px",
                                      borderColor: "#94ba33",
                                      whiteSpace: "nowrap",
                                      marginTop: "0px",
                                      alignSelf: "center",
                                      minWidth: "auto",
                                      marginLeft: "10px",
                                    }}
                                  >
                                    {isDeleteRequest
                                      ? "Delete Request Pending"
                                      : "Request to Delete"}
                                  </Button>
                                )}
                            </div>
                          }
                          size={12}
                        />
                        {index !== activeNoteHistory.length - 1 && (
                          <Divider
                            className="mt-1"
                            style={{ borderColor: "#e0e0e0" }}
                          />
                        )}
                      </>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {showRequestDeleteModal && (
            <RequestDeleteNoteModal
              show={showRequestDeleteModal}
              note={selectedNote}
              onCancel={handleCancelRequestDelete}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PatientNotes;
